/** Base filter applied to listing search + category facet counts. */
export const buildPublishedFiltersString = (extraFilters = []) => {
  let base = 'state:published';
  if (extraFilters?.length) {
    base += ` AND ${extraFilters.join(' AND ')}`;
  }
  return base;
};

/** Deals: service subcategory id saved on linked product/service (`listingsCategories`). */
export const buildDealServiceCategoryFilter = categoryId =>
  `publicData.listingsCategories:${categoryId}`;

/**
 * Params for category-strip facet counts — mirrors active search bar constraints
 * (published, max price, location radius) but not the selected category.
 */
export const buildCategoryStripFacetSearchParams = ({
  facetAttribute,
  filterParams = [],
  aroundLatLng,
  aroundRadius,
}) => {
  const params = {
    query: '',
    hitsPerPage: 0,
    // Also fetch the parent-level facet so parent counts are accurate even when listings use
    // legacy subcategory ids (which wouldn't sum up under the canonical subcategories).
    facets: Array.from(new Set([facetAttribute, 'publicData.categoryLevel1'])),
    maxValuesPerFacet: 200,
    filters: buildPublishedFiltersString(filterParams),
  };

  if (aroundLatLng) {
    params.aroundLatLng = aroundLatLng;
    params.aroundRadius = aroundRadius;
  }

  return params;
};

/**
 * Builds Algolia Configure `filters` string fragments for the listing search bar.
 *
 * @param {Object} params
 * @param {string} params.listingType - 'services' | 'deals' | 'products'
 * @param {string} params.maxPrice - upper price in display currency (SAR), empty = no cap
 * @param {string} params.selectedType - category/deal type id, or 'all'
 * @returns {{ filterParams: string[], facetFilters: string[] }}
 */
export const buildListingSearchFilters = ({
  listingType,
  maxPrice,
  selectedType,
}) => {
  const filterParams = [];
  const priceAttr = 'price.amount';

  const max = Number(maxPrice);
  if (Number.isFinite(max) && max > 0) {
    /* Match working pattern elsewhere in the app (spaces around operators). */
    filterParams.push(`${priceAttr} <= ${Math.round(max * 100)}`);
  }

  let facetFilters = [];
  if (selectedType && selectedType !== 'all') {
    if (listingType === 'deals') {
      /* Use numeric filter string (not facetFilters) for array attribute `listingsCategories`. */
      filterParams.push(buildDealServiceCategoryFilter(selectedType));
    } else if (listingType === 'services') {
      facetFilters = [`publicData.categoryLevel2:${selectedType}`];
    } else if (listingType === 'products') {
      facetFilters = [`publicData.categoryLevel1:${selectedType}`];
    }
  }

  return { filterParams, facetFilters };
};

/** Matches legacy AlgoliaSearchPage / staging: rank & filter within 100 km of selected place center. */
export const LOCATION_SEARCH_RADIUS_METERS = 100000;

/**
 * Builds Algolia Configure `query` for the listing search bar.
 * Location is applied via aroundLatLng + aroundRadius, not as query text.
 */
export const buildLocationSearchQuery = ({ keywordQuery }) => {
  return { query: (keywordQuery || '').trim() };
};
